import CaptiveCarrierOptions from "../../../constants/options/CaptiveCarrierOptions";

const dateStringOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
};

export function interviewToFormData(agentInterview) {
    const answer = questionType => agentInterview?.questionAnswers[questionType]?.answer;

    const workArrangement = _ => {
        if (answer("WorkArrangementRemote") === "true") { return "Remote"; }
        if (answer("WorkArrangementHybrid") === "true") { return "Hybrid"; }
        if (answer("WorkArrangementInOffice") === "true") { return "InOffice"; }
    }

    let captiveCarrier = answer("CaptiveCarrier");
    let captiveCarrierOther = "";

    if (!CaptiveCarrierOptions.some(o => o.value === captiveCarrier)) {
        captiveCarrierOther = captiveCarrier;
        captiveCarrier = "other";
    }

    const dateOfBirth = answer("DateOfBirth") ?
        new Date(answer("DateOfBirth")).toLocaleDateString("en-US", dateStringOptions) :
        null;

    return {
        dateOfBirth: dateOfBirth,
        ssn: answer("Ssn"),
        ssnConfirm: answer("Ssn"),
        isOffshoreResident: false, // Principals should never be offshore residents.
        residentialAddress: {
            country: "US", // Because principals are never offshore, always set this explicitly to "US".
            streetAddressLineOne: answer("ResidentialAddressStreetLineOne"),
            streetAddressLineTwo: answer("ResidentialAddressStreetLineTwo"),
            zipCode: answer("ResidentialAddressZipCode"),
            city: answer("ResidentialAddressCity"),
            state: answer("ResidentialAddressState"),
            county: answer("ResidentialAddressCounty")
        },
        businessInFlorida: answer("BusinessInFlorida"),
        businessInFloridaCounty: answer("FLCounty")?.split(",").map(s => s.trim()),
        timeAtAgency: answer("YearsWithAgency"),
        workArrangement: workArrangement(),
        lobCommercial: answer("SoldLobCommercial") === "true",
        lobHealth: answer("SoldLobHealth") === "true",
        lobHome: answer("SoldLobHome") === "true",
        lobPersonal: answer("SoldLobPersonalAuto") === "true",
        lobOther: answer("SoldLobOther") === "true",
        otherLineOfBusiness: answer("OtherLinesOfBusiness"),
        captiveAgent: answer("CaptiveAgent"),
        captiveCarrier: captiveCarrier,
        captiveCarrierOther: captiveCarrierOther
    };
}

export function principalFormDataToQuestionAnswers(formData) {
    /**
     * Helper function to map a value to question answer format if it exists
     * @param {string} key - The question key/type
     * @param {*} value - The value to map
     * @returns {Object|{}} Question answer object or empty object if value doesn't exist
     */
    const mapIfExists = (key, value) =>
        value !== null && value !== undefined
            ? { [key]: { answer: value.toString() } }
            : {};

    // Map workArrangement to three boolean fields
    const workArrangementRemote = formData.workArrangement === "Remote" ? "true" : "false";
    const workArrangementHybrid = formData.workArrangement === "Hybrid" ? "true" : "false";
    const workArrangementInOffice = formData.workArrangement === "InOffice" ? "true" : "false";

    const captiveCarrier = formData.captiveCarrier === "other" ?
        formData.captiveCarrierOther :
        formData.captiveCarrier;

    const dateOfBirth = formData.dateOfBirth ?
        new Date(formData.dateOfBirth).toLocaleDateString("en-US", dateStringOptions) :
        null;

    return {
        ...mapIfExists("DateOfBirth", dateOfBirth),
        ...mapIfExists("Ssn", formData.ssn),
        ...mapIfExists("ResidentialAddressCountry", "US"), // always "US" for principals
        ...mapIfExists("ResidentialAddressStreetLineOne", formData.residentialAddress?.streetAddressLineOne),
        ...mapIfExists("ResidentialAddressStreetLineTwo", formData.residentialAddress?.streetAddressLineTwo),
        ...mapIfExists("ResidentialAddressZipCode", formData.residentialAddress?.zipCode),
        ...mapIfExists("ResidentialAddressCity", formData.residentialAddress?.city),
        ...mapIfExists("ResidentialAddressState", formData.residentialAddress?.state),
        ...mapIfExists("ResidentialAddressCounty", formData.residentialAddress?.county),
        ...mapIfExists("BusinessInFlorida", formData.businessInFlorida),
        ...mapIfExists("FLCounty", formData.businessInFloridaCounty),
        ...mapIfExists("YearsWithAgency", formData.timeAtAgency?.toString()),
        WorkArrangementRemote: { answer: workArrangementRemote },
        WorkArrangementHybrid: { answer: workArrangementHybrid },
        WorkArrangementInOffice: { answer: workArrangementInOffice },
        ...mapIfExists("SoldLobCommercial", formData.lobCommercial?.toString()),
        ...mapIfExists("SoldLobHealth", formData.lobHealth?.toString()),
        ...mapIfExists("SoldLobHome", formData.lobHome?.toString()),
        ...mapIfExists("SoldLobPersonalAuto", formData.lobPersonal?.toString()),
        ...mapIfExists("SoldLobOther", formData.lobOther?.toString()),
        ...mapIfExists("OtherLinesOfBusiness", formData.otherLineOfBusiness),
        ...mapIfExists("CaptiveAgent", formData.captiveAgent?.toString()),
        ...mapIfExists("CaptiveCarrier", captiveCarrier)
    };
}